import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize, Settings } from 'lucide-react';
import SubtitleOverlay from './SubtitleOverlay';
import VideoLanguageSelector from './VideoLanguageSelector';
import DynamicWatermark from './DynamicWatermark';

const PlayerContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  padding: 20px 16px 16px;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 10;
`;

const ProgressContainer = styled.div`
  margin-bottom: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.primary};
  border-radius: 2px;
  width: ${props => props.progress}%;
  transition: width 0.1s ease;
`;

const ProgressHandle = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props.progress}%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: ${props => props.theme.primary};
  border-radius: 50%;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ProgressBar}:hover & {
    opacity: 1;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.2);
  }

  &.play {
    width: 44px;
    height: 44px;
  }
`;

const TimeDisplay = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 4px;
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: ${props => props.theme.primary};
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: ${props => props.theme.primary};
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const SpeedSelect = styled.select`
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;

  option {
    background: #333;
    color: white;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
`;

const VideoPlayer = ({
  videoPath,
  project, // Add project prop to access videoFile
  isPlaying,
  currentTime,
  onTimeUpdate,
  onDurationChange,
  onPlayPause,
  selectedLanguage,
  subtitleStyle,
  watermarkSettings,
  onUpdate
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // start false - only true when URL is loading
  const [hideControlsTimeout, setHideControlsTimeout] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [currentSubtitleLanguage, setCurrentSubtitleLanguage] = useState(selectedLanguage);
  const [languageSelectorOpen, setLanguageSelectorOpen] = useState(false);
  const [playerDimensions, setPlayerDimensions] = useState({ width: 640, height: 360 });
  const [videoNaturalDimensions, setVideoNaturalDimensions] = useState({ width: 0, height: 0 });
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [videoElement, setVideoElement] = useState(null);

  const setVideoRef = (el) => {
    videoRef.current = el;
    setVideoElement(el);
  };

  const handleLayerMouseDown = (e, layerId) => {
    if (layerId === 'bg') return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedLayerId(layerId);
    
    const formatOpts = project?.settings || {};
    const customization = formatOpts?.customization || {};
    const layers = customization?.layers || [];
    
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLayerX = layer.x;
    const startLayerY = layer.y;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const rect = absoluteCropBox;

      const pctX = (deltaX / rect.width) * 100;
      const pctY = (deltaY / rect.height) * 100;

      let newX = Math.round(startLayerX + pctX);
      let newY = Math.round(startLayerY + pctY);
      
      newX = Math.max(-400, Math.min(400, newX));
      newY = Math.max(-400, Math.min(400, newY));

      const updated = layers.map(l => l.id === layerId ? { ...l, x: newX, y: newY } : l);
      
      if (onUpdate) {
        onUpdate({
          settings: {
            ...project.settings,
            customization: {
              ...project.settings?.customization,
              layers: updated
            }
          }
        });
      }
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeMouseDown = (e, layerId, position) => {
    e.stopPropagation();
    e.preventDefault();
    
    const formatOpts = project?.settings || {};
    const customization = formatOpts?.customization || {};
    const layers = customization?.layers || [];
    
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLayerX = layer.x;
    const startLayerY = layer.y;
    const startW = layer.w;
    const startH = layer.h;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const rect = absoluteCropBox;

      const pctX = (deltaX / rect.width) * 100;
      const pctY = (deltaY / rect.height) * 100;

      let newX = startLayerX;
      let newY = startLayerY;
      let newW = startW;
      let newH = startH;

      if (position === 'bottom-right') {
        newW = Math.round(startW + pctX);
        newH = Math.round(startH + pctY);
      } else if (position === 'bottom-left') {
        newX = Math.round(startLayerX + pctX);
        newW = Math.round(startW - pctX);
        newH = Math.round(startH + pctY);
      } else if (position === 'top-right') {
        newY = Math.round(startLayerY + pctY);
        newW = Math.round(startW + pctX);
        newH = Math.round(startH - pctY);
      } else if (position === 'top-left') {
        newX = Math.round(startLayerX + pctX);
        newY = Math.round(startLayerY + pctY);
        newW = Math.round(startW - pctX);
        newH = Math.round(startH - pctY);
      }

      newW = Math.max(5, Math.min(500, newW));
      newH = Math.max(5, Math.min(500, newH));

      const updated = layers.map(l => l.id === layerId ? { ...l, x: newX, y: newY, w: newW, h: newH } : l);
      
      if (onUpdate) {
        onUpdate({
          settings: {
            ...project.settings,
            customization: {
              ...project.settings?.customization,
              layers: updated
            }
          }
        });
      }
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Sync natural video dimensions if metadata already loaded
  useEffect(() => {
    const video = videoElement;
    if (video && video.videoWidth) {
      setVideoNaturalDimensions({
        width: video.videoWidth,
        height: video.videoHeight
      });
    }
  }, [videoElement, videoUrl]);

  // Measure the actual player container dimensions for watermark positioning
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setPlayerDimensions({ width, height });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Resolve video URL from project data
  useEffect(() => {
    let active = true;

    const resolveUrl = async () => {
      // Priority 1: project.videoFile.url (set by ProjectContext from LocalAgent or IndexedDB)
      if (project?.videoFile?.url) {
        if (active) {
          console.log('🎬 VideoPlayer: Using project.videoFile.url:', project.videoFile.url);
          setVideoUrl(project.videoFile.url);
          setIsLoading(true);
        }
        return;
      }

      // Priority 2: videoPath itself is already an http or blob URL
      if (typeof videoPath === 'string' &&
          (videoPath.startsWith('http') || videoPath.startsWith('blob:'))) {
        if (active) {
          console.log('🎬 VideoPlayer: Using videoPath directly as URL:', videoPath);
          setVideoUrl(videoPath);
          setIsLoading(true);
        }
        return;
      }

      // Priority 2b: videoPath is a local file path (macOS/Windows)
      if (typeof videoPath === 'string' && videoPath.trim().length > 0) {
        const streamUrl = `http://localhost:8001/stream-video?path=${encodeURIComponent(videoPath)}`;
        if (active) {
          console.log('🎬 VideoPlayer: Streaming local videoPath via Agent:', streamUrl);
          setVideoUrl(streamUrl);
          setIsLoading(true);
        }
        return;
      }

      // Priority 3: Try IndexedDB (offline browser fallback)
      if (project?.id) {
        try {
          const { getVideoFile } = await import('../utils/videoStorage');
          const idbData = await getVideoFile(project.id);
          if (active) {
            if (idbData?.url) {
              console.log('🎬 VideoPlayer: Using IndexedDB URL for project:', project.id);
              setVideoUrl(idbData.url);
              setIsLoading(true);
            } else {
              console.warn('🎬 VideoPlayer: No video URL found for project:', project.id);
              setVideoUrl(null);
              setIsLoading(false);
            }
          }
        } catch (err) {
          if (active) {
            console.error('🎬 VideoPlayer: Error loading from IndexedDB:', err);
            setVideoUrl(null);
            setIsLoading(false);
          }
        }
        return;
      }

      // No URL found
      if (active) {
        setVideoUrl(null);
        setIsLoading(false);
      }
    };

    resolveUrl();
    return () => { active = false; };
  }, [videoPath, project?.videoFile?.url, project?.id]);

  // Update subtitle language when selectedLanguage prop changes
  useEffect(() => {
    setCurrentSubtitleLanguage(selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    const video = videoElement;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      onDurationChange(video.duration);
      setIsLoading(false);
      setVideoNaturalDimensions({
        width: video.videoWidth,
        height: video.videoHeight
      });
    };

    const handleTimeUpdate = () => {
      onTimeUpdate(video.currentTime);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    if (video.videoWidth) {
      setVideoNaturalDimensions({
        width: video.videoWidth,
        height: video.videoHeight
      });
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoElement, onTimeUpdate, onDurationChange]);

  useEffect(() => {
    const video = videoElement;
    if (!video) return;

    if (isPlaying) {
      video.play().catch(error => {
        console.log('Video play failed:', error);
        setIsLoading(false);
      });
    } else {
      video.pause();
    }
  }, [videoElement, isPlaying]);

  useEffect(() => {
    const video = videoElement;
    if (!video) return;

    if (Math.abs(video.currentTime - currentTime) > 0.5) {
      video.currentTime = currentTime;
    }
  }, [videoElement, currentTime]);

  const handlePlayPause = () => {
    onPlayPause(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    onTimeUpdate(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (videoRef.current) {
      videoRef.current.volume = newMuted ? 0 : volume;
    }
  };

  const handleSpeedChange = (e) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
    if (videoRef.current) {
      videoRef.current.playbackRate = newRate;
    }
  };

  const handleSkipBack = () => {
    const newTime = Math.max(0, currentTime - 10);
    onTimeUpdate(newTime);
  };

  const handleSkipForward = () => {
    const newTime = Math.min(duration, currentTime + 10);
    onTimeUpdate(newTime);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const showControls = () => {
    setControlsVisible(true);
    if (hideControlsTimeout) {
      clearTimeout(hideControlsTimeout);
    }
    if (isPlaying) {
      const timeout = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
      setHideControlsTimeout(timeout);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const formatOpts = project?.settings || {};
  const formatEnabled = formatOpts?.formatSettingsEnabled && formatOpts?.format && formatOpts?.format !== 'original';
  
  const getTargetRatioValue = () => {
    const formatToAspectRatio = {
      '9:16': '9/16',
      '16:9': '16/9',
      '1:1':  '1/1',
      '4:5':  '4/5',
    };
    
    let ratioStr = formatOpts?.aspectRatio || '16/9';
    if (formatOpts?.format === 'advanced') {
      const canvasFmt = formatOpts?.customization?.canvasFormat;
      ratioStr = formatToAspectRatio[canvasFmt] || ratioStr;
      if (canvasFmt === 'custom') {
        const customW = formatOpts?.customWidth || 16;
        const customH = formatOpts?.customHeight || 9;
        ratioStr = `${customW}/${customH}`;
      }
    }
    
    try {
      if (ratioStr.includes('/')) {
        const parts = ratioStr.split('/');
        return parseFloat(parts[0]) / parseFloat(parts[1]);
      }
      if (ratioStr.includes(':')) {
        const parts = ratioStr.split(':');
        return parseFloat(parts[0]) / parseFloat(parts[1]);
      }
      return parseFloat(ratioStr);
    } catch (e) {
      return 16/9;
    }
  };

  const getVideoBox = () => {
    if (!videoNaturalDimensions.width || !videoNaturalDimensions.height) {
      return { left: 0, top: 0, width: playerDimensions.width, height: playerDimensions.height };
    }
    
    const videoAspect = videoNaturalDimensions.width / videoNaturalDimensions.height;
    const containerAspect = playerDimensions.width / playerDimensions.height;
    
    let w, h, l, t;
    if (containerAspect > videoAspect) {
      // Letterbox on left/right
      h = playerDimensions.height;
      w = h * videoAspect;
      l = (playerDimensions.width - w) / 2;
      t = 0;
    } else {
      // Letterbox on top/bottom
      w = playerDimensions.width;
      h = w / videoAspect;
      l = 0;
      t = (playerDimensions.height - h) / 2;
    }
    
    return { left: l, top: t, width: w, height: h };
  };

  const videoBox = getVideoBox();
  const targetRatio = getTargetRatioValue();
  const videoAspect = videoNaturalDimensions.width && videoNaturalDimensions.height 
    ? videoNaturalDimensions.width / videoNaturalDimensions.height 
    : 16/9;

  let cropW = videoBox.width;
  let cropH = videoBox.height;
  let cropLeft = 0;
  let cropTop = 0;

  const customization = formatOpts?.customization || {};
  const layers = customization?.layers || [];
  const faceTrackingEnabled = customization?.faceTrackingEnabled || false;
  const faceTrackingSubMode = customization?.faceTrackingSubMode || 'automatic';

  if (formatEnabled && videoNaturalDimensions.width) {
    if (targetRatio < videoAspect) {
      cropH = videoBox.height;
      cropW = videoBox.height * targetRatio;
      cropTop = 0;
      let manualX = formatOpts.manualCropX !== undefined ? formatOpts.manualCropX : 50;
      if (faceTrackingEnabled && faceTrackingSubMode === 'automatic') {
        manualX = 50;
      }
      cropLeft = (manualX / 100) * (videoBox.width - cropW);
    } else {
      cropW = videoBox.width;
      cropH = videoBox.width / targetRatio;
      cropLeft = 0;
      let manualY = formatOpts.manualCropY !== undefined ? formatOpts.manualCropY : 50;
      if (faceTrackingEnabled && faceTrackingSubMode === 'automatic') {
        manualY = 50;
      }
      cropTop = (manualY / 100) * (videoBox.height - cropH);
    }
  }

  const absoluteCropBox = {
    left: videoBox.left + cropLeft,
    top: videoBox.top + cropTop,
    width: cropW,
    height: cropH
  };

  const getVideoElementStyle = (videoLayer) => {
    if (!videoNaturalDimensions.width || !videoNaturalDimensions.height) {
      return {
        position: 'absolute',
        left: `${videoLayer.x}%`,
        top: `${videoLayer.y}%`,
        width: `${videoLayer.w}%`,
        height: `${videoLayer.h}%`,
        objectFit: 'cover',
        zIndex: videoLayer.zIndex || 1
      };
    }

    const videoAspect = videoNaturalDimensions.width / videoNaturalDimensions.height;
    const layerW = (absoluteCropBox.width * videoLayer.w) / 100;
    const layerH = (absoluteCropBox.height * videoLayer.h) / 100;
    const layerAspect = layerW / layerH;

    let vWidth, vHeight, vLeft, vTop;

    if (layerAspect < videoAspect) {
      vHeight = 100;
      vWidth = (videoAspect / layerAspect) * 100;
      vLeft = (100 - vWidth) / 2;
      vTop = 0;
    } else {
      vWidth = 100;
      vHeight = (layerAspect / videoAspect) * 100;
      vTop = (100 - vHeight) / 2;
      vLeft = 0;
    }

    return {
      position: 'absolute',
      left: `${vLeft}%`,
      top: `${vTop}%`,
      width: `${vWidth}%`,
      height: `${vHeight}%`,
      objectFit: 'fill',
    };
  };

  return (
    <PlayerContainer
      ref={containerRef}
      onMouseMove={showControls}
      onMouseEnter={showControls}
      onMouseLeave={() => isPlaying && setControlsVisible(false)}
    >
      {/* 1. Video Box Clipping Wrapper (Clips all layer overflow to original video dimensions) */}
      {formatEnabled && formatOpts.format === 'advanced' ? (
        <div style={{
          position: 'absolute',
          left: `${videoBox.left}px`,
          top: `${videoBox.top}px`,
          width: `${videoBox.width}px`,
          height: `${videoBox.height}px`,
          overflow: 'hidden',
          zIndex: 3
        }}>
          {/* Canvas Clipping Wrapper */}
          <div style={{
            position: 'absolute',
            left: `${cropLeft}px`,
            top: `${cropTop}px`,
            width: `${cropW}px`,
            height: `${cropH}px`,
            overflow: 'visible'
          }}>
            {/* Canvas Background Layer */}
            {(() => {
              const bgLayer = layers.find(l => l.type === 'background');
              if (!bgLayer || bgLayer.visible === false) return null;
              return (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: bgLayer.color || '#000000',
                  backgroundImage: bgLayer.imagePath ? `url(${bgLayer.imagePath})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  zIndex: bgLayer.zIndex || 0
                }} />
              );
            })()}

            {/* Composed Video Element */}
            {(() => {
              const videoLayer = layers.find(l => l.type === 'video');
              if (!videoLayer || videoLayer.visible === false) return null;
              const videoElStyle = getVideoElementStyle(videoLayer);
              return (
                <div style={{
                  position: 'absolute',
                  left: `${videoLayer.x}%`,
                  top: `${videoLayer.y}%`,
                  width: `${videoLayer.w}%`,
                  height: `${videoLayer.h}%`,
                  overflow: videoLayer.allowOverflow ? 'visible' : 'hidden',
                  zIndex: videoLayer.zIndex || 1
                }}>
                  <VideoElement
                    ref={setVideoRef}
                    src={videoUrl}
                    onClick={handlePlayPause}
                    style={videoElStyle}
                    onError={(e) => {
                      setIsLoading(false);
                      console.error('🎬 VideoPlayer: Failed to load video source:', videoUrl, e);
                    }}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <VideoElement
          ref={setVideoRef}
          src={videoUrl}
          onClick={handlePlayPause}
          onError={(e) => {
            setIsLoading(false);
            console.error('🎬 VideoPlayer: Failed to load video source:', videoUrl, e);
          }}
        />
      )}

      {/* Crop Mask Overlays (Dark overlays for cropped-out areas, covering the entire PlayerContainer) */}
      {formatEnabled && videoNaturalDimensions.width > 0 && (
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 4
        }}>
          {/* Left Mask */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `${absoluteCropBox.left}px`,
            height: '100%',
            background: 'rgba(0, 0, 0, 0.2)'
          }} />
          {/* Right Mask */}
          <div style={{
            position: 'absolute',
            left: `${absoluteCropBox.left + absoluteCropBox.width}px`,
            top: 0,
            right: 0,
            height: '100%',
            background: 'rgba(0, 0, 0, 0.2)'
          }} />
          {/* Top Mask */}
          <div style={{
            position: 'absolute',
            left: `${absoluteCropBox.left}px`,
            top: 0,
            width: `${absoluteCropBox.width}px`,
            height: `${absoluteCropBox.top}px`,
            background: 'rgba(0, 0, 0, 0.2)'
          }} />
          {/* Bottom Mask */}
          <div style={{
            position: 'absolute',
            left: `${absoluteCropBox.left}px`,
            top: `${absoluteCropBox.top + absoluteCropBox.height}px`,
            width: `${absoluteCropBox.width}px`,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.2)'
          }} />
        </div>
      )}

      {/* Active Area Bounding Container (contains Crop Border, Subtitles, Watermarks, and Composition Layers) */}
      <div style={{
        position: 'absolute',
        left: `${absoluteCropBox.left}px`,
        top: `${absoluteCropBox.top}px`,
        width: `${absoluteCropBox.width}px`,
        height: `${absoluteCropBox.height}px`,
        pointerEvents: 'none',
        zIndex: 5,
        border: formatEnabled ? '2px solid #ff6b35' : 'none',
        boxSizing: 'border-box',
        boxShadow: formatEnabled ? '0 0 12px rgba(255, 107, 53, 0.5)' : 'none',
        transition: 'all 0.1s ease'
      }}>
        {/* Format Tag Label */}
        {formatEnabled && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: '#ff6b35',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            padding: '3px 8px',
            borderRadius: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 100
          }}>
            {formatOpts.format}
          </div>
        )}

        {/* Multi-Layer Canvas Composition Guides */}
        {formatEnabled && formatOpts.format === 'advanced' && layers.length > 0 && (
          <div 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              pointerEvents: 'none',
              zIndex: 10
            }}
          >
            {/* Guides wrapper (replaces old cutout wrapper) */}

            {/* 2. Interactive Layers */}
            {layers.filter(l => l.type !== 'background' && l.visible !== false).map((layer) => {
              const isSelected = selectedLayerId === layer.id;
              const isVideo = layer.type === 'video';
              const isText = layer.type === 'text';
              const isLogo = layer.type === 'logo';

              return (
                <div
                  key={layer.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedLayerId(layer.id);
                  }}
                  onMouseDown={(e) => handleLayerMouseDown(e, layer.id)}
                  style={{
                    position: 'absolute',
                    top: `${layer.y}%`,
                    left: `${layer.x}%`,
                    width: `${layer.w}%`,
                    height: `${layer.h}%`,
                    zIndex: layer.zIndex || 1,
                    pointerEvents: 'auto',
                    boxSizing: 'border-box',
                    border: isSelected 
                      ? '2px solid #ff6b35' 
                      : isVideo 
                      ? '2px dashed #3b82f6' 
                      : isText 
                      ? '1px dashed #10b981' 
                      : '1px dashed #f59e0b',
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isText ? (layer.style?.textAlign || 'center') : 'center',
                    overflow: 'visible'
                  }}
                >
                  {/* Label */}
                  <div style={{
                    position: 'absolute',
                    top: '-16px',
                    left: '2px',
                    background: isVideo ? '#3b82f6' : isText ? '#10b981' : '#f59e0b',
                    color: 'white',
                    fontSize: '8px',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    {isVideo && '🎥 Video'}
                    {isText && '💬 Text'}
                    {isLogo && '🖼️ Logo'}
                  </div>

                  {/* Render content based on layer type */}
                  {isText && (
                    <span style={{
                      fontSize: `${layer.style?.fontSize ? layer.style.fontSize * 0.45 : 12}px`,
                      color: layer.style?.color || '#ffffff',
                      fontFamily: layer.style?.fontFamily || 'Arial',
                      fontWeight: layer.style?.fontWeight || 'bold',
                      textAlign: layer.style?.textAlign || 'center',
                      width: '100%',
                      wordBreak: 'break-word',
                      pointerEvents: 'none',
                      whiteSpace: 'normal'
                    }}>
                      {layer.content.replace('{part_name}', project?.name || 'Part Name')}
                    </span>
                  )}

                  {isLogo && layer.imagePath && (
                    <img 
                      src={layer.imagePath} 
                      style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} 
                      alt="Logo" 
                    />
                  )}

                  {/* Corner Resize Handles */}
                  {isSelected && (
                    <>
                      {/* Top Left */}
                      <div 
                        onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'top-left')}
                        style={{ position: 'absolute', width: '8px', height: '8px', background: 'white', border: '2px solid #ff6b35', borderRadius: '50%', zIndex: 10, top: '-4px', left: '-4px', cursor: 'nwse-resize' }} 
                      />
                      {/* Top Right */}
                      <div 
                        onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'top-right')}
                        style={{ position: 'absolute', width: '8px', height: '8px', background: 'white', border: '2px solid #ff6b35', borderRadius: '50%', zIndex: 10, top: '-4px', right: '-4px', cursor: 'nesw-resize' }} 
                      />
                      {/* Bottom Left */}
                      <div 
                        onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'bottom-left')}
                        style={{ position: 'absolute', width: '8px', height: '8px', background: 'white', border: '2px solid #ff6b35', borderRadius: '50%', zIndex: 10, bottom: '-4px', left: '-4px', cursor: 'nesw-resize' }} 
                      />
                      {/* Bottom Right */}
                      <div 
                        onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'bottom-right')}
                        style={{ position: 'absolute', width: '8px', height: '8px', background: 'white', border: '2px solid #ff6b35', borderRadius: '50%', zIndex: 10, bottom: '-4px', right: '-4px', cursor: 'nwse-resize' }} 
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Subtitle Overlay */}
        <SubtitleOverlay
          project={project}
          currentTime={currentTime}
          selectedLanguage={currentSubtitleLanguage}
          subtitleStyle={subtitleStyle}
        />

        {/* Dynamic Watermark(s) */}
        {Array.isArray(watermarkSettings) ? (
          watermarkSettings.map((ws, index) => (
            <DynamicWatermark
              key={index}
              settings={ws}
              currentTime={currentTime}
              videoDimensions={{ width: absoluteCropBox.width, height: absoluteCropBox.height }}
            />
          ))
        ) : (
          <DynamicWatermark
            settings={watermarkSettings}
            currentTime={currentTime}
            videoDimensions={{ width: absoluteCropBox.width, height: absoluteCropBox.height }}
          />
        )}
      </div>

      {/* Language Selector (Removed) */}

      {isLoading && (
        <LoadingOverlay>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px' }}>Loading video...</div>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>Ensure Local Agent is running on port 8001</div>
          </div>
        </LoadingOverlay>
      )}

      {!videoUrl && !isLoading && (
        <LoadingOverlay>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ marginBottom: '10px' }}>No video source available.</div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>
              This project might be missing its media file. <br/>
              Try re-importing the video or checking if the Local Agent is active.
            </div>
          </div>
        </LoadingOverlay>
      )}

      <Controls visible={controlsVisible || !isPlaying}>
        <ProgressContainer>
          <ProgressBar onClick={handleProgressClick}>
            <ProgressFill progress={progress} />
            <ProgressHandle progress={progress} />
          </ProgressBar>
        </ProgressContainer>

        <ControlsRow>
          <ControlsLeft>
            <ControlButton onClick={handleSkipBack}>
              <SkipBack size={20} />
            </ControlButton>
            
            <ControlButton className="play" onClick={handlePlayPause}>
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </ControlButton>
            
            <ControlButton onClick={handleSkipForward}>
              <SkipForward size={20} />
            </ControlButton>

            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </TimeDisplay>
          </ControlsLeft>

          <ControlsRight>
            <VolumeContainer>
              <ControlButton onClick={handleMuteToggle}>
                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </ControlButton>
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
              />
            </VolumeContainer>

            <SpeedSelect value={playbackRate} onChange={handleSpeedChange}>
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </SpeedSelect>

            <ControlButton>
              <Settings size={20} />
            </ControlButton>

            <ControlButton>
              <Maximize size={20} />
            </ControlButton>
          </ControlsRight>
        </ControlsRow>
      </Controls>
    </PlayerContainer>
  );
};

export default VideoPlayer;